import { lazy, type ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { noteNavigation } from '../api/client';
import { notePage } from '../api/prefetch';
import { registerRoutePreload } from './shell/routePreload';
import { AppShell } from './shell/AppShell';
import { RequirePersona } from './auth/RoleContext';
import { TrustScreen } from '../features/overview/TrustScreen';
import { DataQuality } from '../features/quality/DataQuality';
import { MapScreen } from '../features/map/MapScreen';
import { ActorRegistry } from '../features/actors/ActorRegistry';
import { ActorDetail } from '../features/actors/ActorDetail';
import { AgrocycleScreen } from '../features/agrocycle/AgrocycleScreen';
import { PlacementManager } from '../features/placement/PlacementManager';
import { CampaignScreen } from '../features/campaigns/CampaignScreen';
import { SituationBoard as CanonSituationBoard } from '../features/situations/SituationBoard';
import { VerificationScreen } from '../features/verification/VerificationScreen';
import { MyWorks } from '../features/works/MyWorks';
import { WorkDetail as CanonWorkDetail } from '../features/works/WorkDetail';
import { Login } from './pages/Login';
import { Overview } from './pages/Overview';
import { DistrictOverview } from './pages/DistrictOverview';
import { NAV_VERSION } from './shell/navVersion';
import { CycleDetail } from './pages/CycleDetail';
import { WorkDetail } from './pages/WorkDetail';
import { TemplateDetail } from './pages/TemplateDetail';
import { MapStation } from './pages/MapStation';
import { Farms } from './pages/Farms';
import { FarmDetail } from './pages/FarmDetail';
import { MassifDetail } from './pages/MassifDetail';
import { Contours } from './pages/Contours';
import { ContourDetail } from './pages/ContourDetail';
import { Analytics } from './pages/Analytics';
import { NotFound } from './pages/NotFound';
import { DecisionCenter } from './astra/DecisionCenter';
import { CyclesHub } from './pages/cyclesHub/CyclesHub';
import { Planning } from './astra/Planning';
import { Analyst } from './astra/Analyst';
import { Briefing } from './astra/Briefing';
import { Guide } from './astra/Guide';
import { WaterLedger } from './astra/WaterLedger';

/**
 * Ленивая страница кабинета. Экраны /my и /ops пишутся параллельно, и форма
 * их экспорта не должна ломать маршрут: берётся именованный экспорт, а если
 * его нет — default. Отсутствие обоих — понятная ошибка, а не пустой экран.
 */
function lazyPage(load: () => Promise<unknown>, name: string) {
  const Page = lazy(async () => {
    const mod = (await load()) as Record<string, ComponentType | undefined>;
    const picked = mod[name] ?? mod.default;
    if (!picked) throw new Error(`страница «${name}» не экспортирована`);
    return { default: picked };
  });
  // Загрузчик запоминается при компоненте: меню начинает грузить кусок кода
  // по наведению (`shell/routePreload.ts`, agroai-0e8.26).
  CHUNK_LOADERS.set(Page, load);
  return Page;
}

const CHUNK_LOADERS = new Map<ComponentType, () => Promise<unknown>>();

/* Кабинет фермера (ROLES-SPEC: /my/*). */
const Today = lazyPage(() => import('./pages/my/Today'), 'Today');
const Fields = lazyPage(() => import('./pages/my/Fields'), 'Fields');
const ReportFlow = lazyPage(() => import('./pages/my/ReportFlow'), 'ReportFlow');
const History = lazyPage(() => import('./pages/my/History'), 'History');

/* Кабинет управления (ROLES-SPEC: /ops/*). */
const VerifyQueue = lazyPage(() => import('./pages/ops/VerifyQueue'), 'VerifyQueue');
const Campaigns = lazyPage(() => import('./pages/ops/Campaigns'), 'Campaigns');
const Entry = lazyPage(() => import('./pages/ops/Entry'), 'Entry');
const Assignments = lazyPage(() => import('./pages/ops/Assignments'), 'Assignments');
const QuarantineDesk = lazyPage(() => import('./pages/ops/QuarantineDesk'), 'QuarantineDesk');
const TemplateBuilder = lazyPage(() => import('./pages/template/TemplateBuilder'), 'TemplateBuilder');
const WaterImport = lazyPage(() => import('./pages/ops/WaterImport'), 'WaterImport');


/* Сравнение хозяйств: экран станции, доступный хокиму и диспетчеру. */
const SavedReportPage = lazyPage(() => import('./pages/SavedReportPage'), 'SavedReportPage');
const Compare = lazyPage(() => import('./pages/Compare'), 'Compare');

/* Техника и карантин: доменные экраны станции (ROLES-SPEC), фермеру закрыты
   общим правилом guard'а — как и /compare выше. */
const EquipmentPage = lazyPage(() => import('./pages/EquipmentPage'), 'EquipmentPage');
const QuarantinePage = lazyPage(() => import('./pages/QuarantinePage'), 'QuarantinePage');

/* Ирригация: тот же класс доменного раздела станции. Вода фермера живёт в его
   кабинете, поэтому маршрут закрыт фермеру общим правилом guard'а. */
const WaterPage = lazyPage(() => import('./pages/WaterCanon'), 'WaterCanon');

/* Отчёт для области: файл пишет параллельная волна, маршрут уже назван. */
const ReportPakhtaGalla = lazyPage(
  () => import('./pages/ReportPakhtaGalla'),
  'ReportPakhtaGalla',
);

/**
 * ЕДИНСТВЕННАЯ таблица маршрутов приложения.
 *
 * Второй список адресов рядом расходится с первым молча: в прототипе дымовой
 * прогон держал свою копию, и `/cycles/:id` вёл там на другой экран — то есть
 * проверялось не то приложение, которое собиралось. Поэтому адреса объявлены
 * один раз здесь, и отсюда же их читают все остальные: таблица источников
 * (`data-source/routes.ts`, сверяется `routes.test.ts`) и выгрузка для
 * сервера (`scripts/export-levels.mjs` → `lib/levels.export.json`).
 *
 * Сам дымовой прогон прототипа (`prototype/app/src/smoke.tsx`) в перенос не
 * вошёл; вместе с ним ушла и подстановка экранов в `AppRoutes` — у неё не
 * осталось потребителя.
 */
const PAGES = {
  login: Login,
  savedReport: SavedReportPage,
  home: DistrictOverview,
  season: DecisionCenter,
  district: DistrictOverview,
  guide: Guide,
  overview: Overview,
  cycles: CyclesHub,
  cycle: CycleDetail,
  work: WorkDetail,
  template: TemplateDetail,
  map: MapStation,
  farms: Farms,
  farm: FarmDetail,
  massif: MassifDetail,
  contours: Contours,
  contour: ContourDetail,
  water: WaterPage,
  equipment: EquipmentPage,
  quarantine: QuarantinePage,
  analyst: Analyst,
  waterLedger: WaterLedger,
  planning: Planning,
  briefing: Briefing,
  explore: Analytics,
  compare: Compare,
  pakhtaGalla: ReportPakhtaGalla,
  myToday: Today,
  myFields: Fields,
  myReport: ReportFlow,
  myHistory: History,
  opsAssignments: Assignments,
  opsVerify: VerifyQueue,
  opsCampaigns: Campaigns,
  opsEntry: Entry,
  opsTemplates: TemplateBuilder,
  opsPlacement: PlacementManager,
  opsWater: WaterImport,
  opsQuarantine: QuarantineDesk,
  // Раздел «Данные»: экраны каноники остаются продуктом, а не архивом.
  dataRegistry: TrustScreen,
  dataQuality: DataQuality,
  dataMap: MapScreen,
  // Канонические экраны вне навигации — см. CANON_ROUTES ниже.
  canonActors: ActorRegistry,
  canonActor: ActorDetail,
  canonAgrocycle: AgrocycleScreen,
  canonCampaigns: CampaignScreen,
  canonSituations: CanonSituationBoard,
  canonVerification: VerificationScreen,
  canonFarmWorks: MyWorks,
  canonWork: CanonWorkDetail,
  notFound: NotFound,
} satisfies Record<string, ComponentType>;

export type PageName = keyof typeof PAGES;

export interface RouteSpec {
  path: string;
  page: PageName;
}

/**
 * Вход живёт ВНЕ гейта и вне оболочки: сайдбар и топбар говорят от имени
 * персоны, которой на этом экране ещё нет.
 */
/**
 * `/reports/:ref` (agroai-6al.57) — сохранённый отчёт ИИ: по токену публичной
 * ссылки открывается без входа (решение владельца, журнал №601), по id
 * сущности — автору. Стоит вне оболочки: аноним не должен попадать на вход.
 */
export const PUBLIC_ROUTES: RouteSpec[] = [
  { path: '/login', page: 'login' },
  { path: '/reports/:ref', page: 'savedReport' },
];

/**
 * Канонические экраны, оставшиеся АДРЕСУЕМЫМИ, но убранные из навигации.
 *
 * Правка владельца 17.09 звучит в обе стороны: ни один экран Astra не
 * исчезает — и канонические экраны тоже не выбрасываются, пока фаза 4 не
 * подведёт итог. Из сайдбара они ушли (там поверхность Astra плюс раздел
 * «Данные»), но адрес у них остался, и по ссылке они открываются.
 *
 * У этого есть вторая причина, техническая и жёсткая: список адресов клиента
 * уезжает на сервер (`lib/levels.export.json` → `ai_gateway/validator.py`) и
 * там решает, какую ссылку инструменту реестра разрешено вернуть. Выкинуть
 * адрес из таблицы значит запретить серверу ссылаться на существующий экран —
 * то есть сломать чужой контракт ради пункта меню.
 *
 * Здесь только те адреса, которые НЕ пересекаются с поверхностью Astra:
 * пересекающиеся (`/`, `/contours`, `/massifs/:id`, `/quarantine`, `/map`,
 * `/guide`, `/analytics`, `/my/*`) заняты экранами Astra, и это и есть смена
 * поверхности продукта.
 */
const CANON_ROUTES: RouteSpec[] = [
  { path: '/actors', page: 'canonActors' },
  { path: '/actors/:actorId', page: 'canonActor' },
  { path: '/agrocycle', page: 'canonAgrocycle' },
  { path: '/campaigns', page: 'canonCampaigns' },
  { path: '/situations', page: 'canonSituations' },
  { path: '/verification', page: 'canonVerification' },
  { path: '/my-works', page: 'canonFarmWorks' },
  { path: '/works/:workId', page: 'canonWork' },
];

/** Всё остальное — за `RequirePersona` внутри `AppShell`. */
export const SHELL_ROUTES: RouteSpec[] = [
  // `/` — общий дом обеих станционных ролей: одна «Картина сезона».
  // Разделения по роли здесь нет; различие хокима и диспетчера живёт в
  // разделе «Управление» сайдбара.
  { path: '/', page: 'home' },
  // Обзор исходного реестра сохраняет собственный адрес: «/» занят картиной
  // сезона, а реестр района никуда не делся.
  // «Обзор района» (agroai-0yt.8): временный адрес до переноса на `/`.
  { path: '/district', page: 'district' },
  // Прежняя «Картина сезона»: из меню ушла, адрес остаётся (откат и прямые ссылки).
  { path: '/season', page: 'season' },
  { path: '/guide', page: 'guide' },
  { path: '/overview', page: 'overview' },
  { path: '/cycles', page: 'cycles' },
  { path: '/cycles/:id', page: 'cycle' },
  { path: '/cycles/:id/works/:workId', page: 'work' },
  { path: '/templates/:id', page: 'template' },
  { path: '/map', page: 'map' },
  // Списочные экраны нужны пунктам сайдбара: без них «Хозяйства» и «Реестр
  // контуров» вели бы в никуда.
  { path: '/farms', page: 'farms' },
  { path: '/farms/:id', page: 'farm' },
  { path: '/massifs/:id', page: 'massif' },
  { path: '/contours', page: 'contours' },
  { path: '/contours/:id', page: 'contour' },
  { path: '/water', page: 'water' },
  { path: '/equipment', page: 'equipment' },
  { path: '/quarantine', page: 'quarantine' },
  { path: '/analytics', page: 'analyst' },
  { path: '/analysis/water', page: 'waterLedger' },
  { path: '/planning', page: 'planning' },
  { path: '/reports/briefing', page: 'briefing' },
  { path: '/explore', page: 'explore' },
  // Фермеру оба экрана закрыты общим правилом guard'а: его уровень
  // заканчивается своим хозяйством, а здесь район.
  { path: '/compare', page: 'compare' },
  { path: '/reports/pakhta-galla', page: 'pakhtaGalla' },

  { path: '/my/today', page: 'myToday' },
  { path: '/my/fields', page: 'myFields' },
  { path: '/my/report/:cycleId/:workId', page: 'myReport' },
  { path: '/my/history', page: 'myHistory' },

  { path: '/ops/assignments', page: 'opsAssignments' },
  { path: '/ops/verify', page: 'opsVerify' },
  { path: '/ops/campaigns', page: 'opsCampaigns' },
  { path: '/ops/entry', page: 'opsEntry' },
  { path: '/ops/templates', page: 'opsTemplates' },
  { path: '/ops/placement', page: 'opsPlacement' },
  { path: '/ops/water', page: 'opsWater' },
  { path: '/ops/quarantine', page: 'opsQuarantine' },

  /*
   * Раздел «Данные» — канонические экраны прежнего фронта.
   *
   * `/registry` и `/data-quality` сохраняют свои адреса: у Astra таких
   * экранов нет, и вопрос «как собран канон» задать больше негде.
   * Канонической карте пришлось переехать на `/data-map`: адрес `/map` в
   * поверхности Astra занят картой-станцией, а ни один экран Astra не
   * исчезает. Два кадра сводятся в один движок фазой 2 (решение 9).
   *
   * Именно `/data-map`, а не `/data/map`: префикс `/data/` — корень статики
   * фикстур прототипа (плагин `proto-data` в `vite.config.ts`), и адрес под
   * ним отвечал бы 404 «фикстура не найдена» вместо экрана.
   */
  { path: '/registry', page: 'dataRegistry' },
  { path: '/data-quality', page: 'dataQuality' },
  { path: '/data-map', page: 'dataMap' },

  ...CANON_ROUTES,

  { path: '*', page: 'notFound' },
];

// Ленивые страницы меню грузят свой кусок кода по наведению (routePreload).
for (const route of SHELL_ROUTES) {
  const load = CHUNK_LOADERS.get(PAGES[route.page]);
  if (load) registerRoutePreload(route.path, load);
}

/**
 * Адреса, которые меню v2 не выводит и которые в v2 ведут на страницу-преемника
 * (план страниц v2, §13). В v1 (`VITE_NAV=v1`) таблица не действует — откат
 * меню не меняется. Экран «Реестр района» (`/overview`) в v2 заменён реестром
 * хозяйств; маршрут остаётся для v1 и валидатора ИИ.
 */
export const V2_REDIRECTS: Partial<Record<PageName, string>> = {
  overview: '/farms',
};

/** Куда в v2 уходит страница, или null, если она открывается как есть. */
export function v2RedirectTarget(name: PageName, navVersion: string = NAV_VERSION): string | null {
  return navVersion === 'v2' ? (V2_REDIRECTS[name] ?? null) : null;
}

/** Редирект на `/` с сохранением среза; «К совещанию» добавляет `mode=print`. */
function NavRedirect({ print }: { print: boolean }) {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  if (print) query.set('mode', 'print');
  const text = query.toString();
  return <Navigate to={{ pathname: '/', search: text ? `?${text}` : '' }} replace />;
}

/** Редирект на другой адрес с сохранением среза (`?as_of=…`). */
function SearchRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

/** Дерево `<Routes>` приложения: один список адресов на всё приложение. */
export function AppRoutes() {
  // Новая навигация — новая проверка версии данных перед показом запомненных
  // ответов (api/client, agroai-6al.54). Зовётся при отрисовке, а не в
  // эффекте: эффекты экранов-потомков выполняются РАНЬШЕ эффекта родителя и
  // успели бы взять прежнее подтверждение. Вызов идемпотентен по ключу.
  const location = useLocation();
  noteNavigation(location.key);
  // Опись первого экрана страницы для предзагрузки по меню (api/prefetch,
  // agroai-0e8.26): ответы, прочитанные экраном сейчас, пишутся под её адрес.
  notePage(location.pathname, location.search);
  const render = (name: PageName) => {
    // Единая точка отката `VITE_NAV` (shell/navVersion.ts): v1 возвращает
    // прежнюю главную; в v2 старые адреса `/district` и `/reports/briefing`
    // становятся редиректами на новый Обзор района.
    if (NAV_VERSION === 'v1' && name === 'home') return <DecisionCenter />;
    if (NAV_VERSION === 'v2' && (name === 'district' || name === 'briefing')) {
      return <NavRedirect print={name === 'briefing'} />;
    }
    const target = v2RedirectTarget(name);
    if (target) return <SearchRedirect to={target} />;
    const Page = PAGES[name];
    return <Page />;
  };
  return (
    <Routes>
      {PUBLIC_ROUTES.map(r => (
        <Route key={r.path} path={r.path} element={render(r.page)} />
      ))}
      <Route element={<RequirePersona />}>
        <Route element={<AppShell />}>
          {SHELL_ROUTES.map(r => (
            <Route key={r.path} path={r.path} element={render(r.page)} />
          ))}
        </Route>
      </Route>
    </Routes>
  );
}
